/**
 * Where a layer's frame sits on the canvas, and how it is turned.
 *
 * One definition, used by everything that draws a layer or draws over one, for
 * a reason found the hard way: the canvas used to rotate a layer about its
 * top-left while both press sinks rotated it about its middle. A bar turned
 * ninety degrees appeared in one place on screen and printed twenty-four
 * millimetres away, and nothing said so - the customer sees the canvas and the
 * press sees the file.
 *
 * Rotating about the middle is the convention every design tool follows, and it
 * is what the rotator handle implies as it orbits the object. So the canvas was
 * the side that moved.
 *
 * Konva composes its transform as translate(position), rotate, scale,
 * translate(-offset). Putting the offset at the middle and the position at the
 * middle makes that identical to what the SVG writes:
 *
 *   translate(x y) rotate(a w/2 h/2) [ translate(mid) scale(±1) translate(-mid) ]
 *
 * Both work out to translate(x + mid) ∘ rotate ∘ scale ∘ translate(-mid), which
 * is worth checking on paper before changing anything here.
 */

import type { Layer } from '../model/types';
import { mmToPx } from '../model/units';

export interface Frame {
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

export function frameOf( layer: Layer, zoom: number ): Frame {
  const w = mmToPx( layer.w, zoom );
  const h = mmToPx( layer.h, zoom );

  return {
    // The position is the middle, because the offset is.
    x: mmToPx( layer.x, zoom ) + w / 2,
    y: mmToPx( layer.y, zoom ) + h / 2,
    offsetX: w / 2,
    offsetY: h / 2,
    rotation: layer.rotation,
    scaleX: layer.flipX ? -1 : 1,
    scaleY: layer.flipY ? -1 : 1,
  };
}

/**
 * The layer's own top-left, from a node placed by frameOf.
 *
 * Dragging reads the node's position, and the node's position is its middle -
 * so anything comparing it with a layer's x and y has to come back through
 * here first.
 */
export function cornerOf( node: { x(): number; y(): number }, layer: Layer, zoom: number ) {
  return {
    x: node.x() - mmToPx( layer.w, zoom ) / 2,
    y: node.y() - mmToPx( layer.h, zoom ) / 2,
  };
}

/** And the other way, for putting a snapped position back onto the node. */
export function middleOf( x: number, y: number, layer: Layer, zoom: number ) {
  return {
    x: x + mmToPx( layer.w, zoom ) / 2,
    y: y + mmToPx( layer.h, zoom ) / 2,
  };
}
